import type { ReactNode } from "react";
import {
  ReportHeader,
  ReportMap,
  ReportFooter,
} from "../../../Components/ReportPreview/components";
import type { ProjecaoReportData } from "./types";
import {
  fmtArea,
  fmtPower,
  fmtEnergy,
  fmtCurrency,
  OPERATOR_LABELS,
  ENERGY_TYPE_LABELS,
} from "./format";

function operatorSummary(filter: ProjecaoReportData["rasterFilters"][number]) {
  const { operator, value, valueMax, unit } = filter;
  if (operator === "entre") return `Entre ${value} e ${valueMax} ${unit}`;
  return `${OPERATOR_LABELS[operator] ?? operator} ${value} ${unit}`;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="report-section text-[1.1rem] font-semibold text-[#0C2856] mt-6 mb-2.5 pb-1.5 border-b-2 border-[#333]">
      {children}
    </h2>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-1 text-[0.85rem] border-b border-gray-100 last:border-b-0">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-[#0C2856]">{value}</span>
    </div>
  );
}

export function ProjecaoReportBody({ data }: { data: ProjecaoReportData }) {
  const hasRestrictions =
    data.rasterFilters.length > 0 ||
    data.vectorMasks.length > 0 ||
    data.kmzMasks.length > 0 ||
    data.infraLayers.length > 0 ||
    data.aneelBufferMinDistKm != null;

  return (
    <>
      <ReportHeader
        projectName={data.metadata.projectName}
        projectDescription={data.metadata.projectDescription}
        timestamp={data.metadata.timestamp}
      />

      {data.mapImage && (
        <ReportMap
          imageUrl={data.mapImage}
          drawingName={data.metadata.projectName}
          drawingAreaKm2={data.areaKm2 ?? undefined}
        />
      )}

      <section className="report-section mb-2">
        <SectionTitle>Configuração da projeção</SectionTitle>
        <InfoRow
          label="Tipo(s) de energia"
          value={
            data.energyTypes.length > 0
              ? data.energyTypes
                  .map((t) => ENERGY_TYPE_LABELS[t] ?? t)
                  .join(" + ")
              : "Nenhum"
          }
        />
        {data.turbine && (
          <>
            <InfoRow label="Modelo de turbina" value={data.turbine.label} />
            <InfoRow
              label="Integração Weibull"
              value={data.turbine.useWeibull ? "Sim" : "Não"}
            />
          </>
        )}
      </section>

      {hasRestrictions && (
        <section className="report-section mb-2">
          <SectionTitle>Restrições aplicadas</SectionTitle>

          {data.rasterFilters.length > 0 && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Raster
              </p>
              {data.rasterFilters.map((f) => (
                <InfoRow key={f.key} label={f.label} value={operatorSummary(f)} />
              ))}
            </>
          )}

          {data.vectorMasks.length > 0 && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Vetoriais
              </p>
              {data.vectorMasks.map((m, i) => (
                <InfoRow
                  key={`${m.title}-${i}`}
                  label={m.title}
                  value={`${m.inside ? "Dentro de" : "Fora de"} ${m.bufferKm} km`}
                />
              ))}
            </>
          )}

          {data.kmzMasks.length > 0 && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Arquivos KMZ / KML
              </p>
              {data.kmzMasks.map((m, i) => (
                <InfoRow
                  key={`${m.fileName}-${i}`}
                  label={m.fileName}
                  value={`${m.inside ? "Dentro de" : "Fora de"} ${m.bufferKm} km`}
                />
              ))}
            </>
          )}

          {data.infraLayers.length > 0 && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Infraestrutura elétrica
              </p>
              {data.infraLayers.map((l, i) => (
                <InfoRow
                  key={`${l.title}-${i}`}
                  label={l.title}
                  value={`Entre ${l.minDistKm} e ${l.maxDistKm} km`}
                />
              ))}
            </>
          )}

          {data.aneelBufferMinDistKm != null && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Distanciamento ANEEL
              </p>
              <InfoRow
                label="Parques eólicos existentes"
                value={`${Math.round(data.aneelBufferMinDistKm * 1000)} m`}
              />
            </>
          )}
        </section>
      )}

      {data.results.map((result) => (
        <section
          key={result.energyType}
          className="report-section mb-2 break-inside-avoid"
        >
          <SectionTitle>
            Resultado — {ENERGY_TYPE_LABELS[result.energyType] ?? result.energyType}
          </SectionTitle>

          <InfoRow label="Área total" value={fmtArea(result.totalAreaKm2)} />
          <InfoRow
            label="Excluído (raster)"
            value={fmtArea(result.excludedRasterKm2)}
          />
          <InfoRow
            label="Excluído (vetorial)"
            value={fmtArea(result.excludedVectorKm2)}
          />
          <InfoRow
            label="Área propícia"
            value={fmtArea(result.propitiousAreaKm2)}
          />
          <InfoRow
            label="Potencial instalável"
            value={fmtPower(result.installableGw)}
          />
          <InfoRow
            label="Energia anual estimada"
            value={fmtEnergy(result.annualEnergyGwh)}
          />
          {result.capacityFactor != null && (
            <InfoRow
              label="Fator de capacidade (Weibull)"
              value={`${(result.capacityFactor * 100).toFixed(1)} %`}
            />
          )}

          {result.financial && (
            <>
              <p className="text-[0.8rem] font-semibold text-gray-500 mt-2 mb-1">
                Análise financeira
              </p>
              <InfoRow
                label="Economia no 1º ano"
                value={fmtCurrency(result.financial.annual_savings_rs)}
              />
              <InfoRow
                label="Autoconsumo"
                value={`${result.financial.self_consumption_fraction_pct.toFixed(
                  1
                )} %`}
              />
              <InfoRow
                label="Payback simples"
                value={
                  result.financial.payback_years != null
                    ? `${result.financial.payback_years} anos e ${result.financial.payback_months} meses`
                    : "> horizonte do projeto"
                }
              />
              <InfoRow
                label="TIR (20 anos)"
                value={
                  result.financial.irr_pct != null
                    ? `${result.financial.irr_pct.toFixed(2)} % a.a.`
                    : "—"
                }
              />
              <InfoRow
                label="Custo de geração (LCOE)"
                value={
                  result.financial.lcoe_rs_kwh != null
                    ? `R$ ${result.financial.lcoe_rs_kwh.toFixed(3)} /kWh`
                    : "—"
                }
              />
              <InfoRow
                label="CO₂ evitado"
                value={`${(
                  result.financial.co2_avoided_kg_year / 1000
                ).toFixed(1)} t/ano`}
              />
            </>
          )}
        </section>
      ))}

      <ReportFooter />
    </>
  );
}
